<template>
  <teleport v-if="mode === 'modal' && isOpen" to="body">
    <div class="demo-modal-backdrop" role="presentation" @click.self="close">
      <section class="demo-modal" role="dialog" aria-modal="true" aria-labelledby="modal-demo-title">
        <button class="modal-close" type="button" aria-label="Cerrar formulario" @click="close">
          <i class="fa-solid fa-xmark"></i>
        </button>
        <DemoLeadFormContent
          id-prefix="modal-demo"
          title="Solicitar demo"
          text="Comparte tus datos y te contactaremos para coordinar una demostracion de CrediCore."
          :submitting="submitting"
          :success-message="successMessage"
          :error-message="errorMessage"
          :initial-message="initialMessage"
          :show-product-select="showProductSelect"
          :product-options="productOptions"
          :products-loading="productsLoading"
          :initial-product-id="currentProductId"
          @submit-lead="submitLead"
        />
      </section>
    </div>
  </teleport>

  <section v-else-if="mode === 'inline'" class="inline-shell">
    <DemoLeadFormContent
      id-prefix="inline-demo"
      title="Solicita una demo de CrediCore"
      text="Dejanos tus datos y revisamos contigo como ordenar creditos, pagos y cobranza."
      :submitting="submitting"
      :success-message="successMessage"
      :error-message="errorMessage"
      :initial-message="initialMessage"
      :show-product-select="showProductSelect"
      :product-options="productOptions"
      :products-loading="productsLoading"
      :initial-product-id="currentProductId"
      @submit-lead="submitLead"
    />
  </section>
</template>

<script>
import DemoLeadFormContent from './DemoLeadFormContent.vue'

const buildDemoEndpoint = (path) => {
  const baseUrl = process.env.VUE_APP_DEMO_LEADS_ENDPOINT || 'http://localhost:8082'

  return `${baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`
}

export default {
  name: 'DemoLeadForm',
  components: {
    DemoLeadFormContent,
  },
  props: {
    mode: {
      type: String,
      default: 'inline',
      validator: (value) => ['inline', 'modal'].includes(value),
    },
    endpoint: {
      type: String,
      default: () => buildDemoEndpoint('leads'),
    },
    tenant: {
      type: String,
      default: 'axum',
    },
    productId: {
      type: [Number, String],
      default: 1,
    },
    estadoId: {
      type: Number,
      default: 1,
    },
    empresaSlug: {
      type: String,
      default: 'axum',
    },
    fuente: {
      type: String,
      default: 'web',
    },
    initialMessage: {
      type: String,
      default: 'Quiero probar la demo',
    },
    showProductSelect: {
      type: Boolean,
      default: false,
    },
    productOptions: {
      type: Array,
      default: () => [],
    },
    productsLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      submitting: false,
      successMessage: '',
      errorMessage: '',
    }
  },
  computed: {
    currentProductId() {
      return this.productId || ''
    },
  },
  methods: {
    open() {
      this.clearMessages()
      this.isOpen = true
      document.body.classList.add('demo-modal-open')
    },
    close() {
      this.isOpen = false
      document.body.classList.remove('demo-modal-open')
    },
    clearMessages() {
      this.successMessage = ''
      this.errorMessage = ''
    },
    normalizePhoneCode(value) {
      const match = String(value || '').match(/\+\d+/)

      return match ? match[0] : value
    },
    async submitLead(form, resetForm) {
      this.clearMessages()
      this.submitting = true

      const payload = {
        product_id: Number(form.product_id || this.productId),
        estado_id: this.estadoId,
        empresa_slug: this.empresaSlug,
        captcha_token: '',
        tipo_documento: form.tipo_documento,
        no_documento: form.no_documento,
        nombre: form.nombre,
        apellido: form.apellido,
        email: form.email,
        codigo_telefono: this.normalizePhoneCode(form.codigo_telefono),
        telefono: form.telefono,
        nombre_empresa: form.nombre_empresa,
        website_empresa: form.website_empresa,
        tamanio_empresa: form.tamanio_empresa,
        fuente: form.fuente || this.fuente,
        mensaje: form.mensaje,
      }

      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant': this.tenant,
          },
          body: JSON.stringify(payload),
        })

        if (!response.ok) {
          throw new Error(`No se pudo enviar la solicitud (${response.status})`)
        }

        this.successMessage = 'Solicitud enviada. Te contactaremos pronto.'
        resetForm()
      } catch (error) {
        this.errorMessage = error.message || 'No se pudo enviar la solicitud. Intentalo nuevamente.'
      } finally {
        this.submitting = false
      }
    },
  },
  beforeUnmount() {
    document.body.classList.remove('demo-modal-open')
  },
}
</script>

<style scoped>
.inline-shell {
  width: 100%;
}

.demo-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background-color: rgba(20, 33, 61, 0.58);
  backdrop-filter: blur(10px);
}

.demo-modal {
  position: relative;
  width: min(900px, 100%);
  max-height: min(92vh, 860px);
  overflow-y: auto;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 0 32px 80px rgba(20, 33, 61, 0.28);
}

.modal-close {
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 1;
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #14213d;
  border: 1px solid #dbdbdb;
  border-radius: 50%;
  background-color: #ffffff;
}
</style>

<style>
body.demo-modal-open {
  overflow: hidden;
}

.demo-form {
  color-scheme: light;
  padding: 30px;
  border: 1px solid rgba(20, 33, 61, 0.1);
  border-radius: 8px;
  background-color: #ffffff;
}

.form-heading {
  max-width: 640px;
  margin-bottom: 24px;
}

.form-heading span {
  display: inline-flex;
  align-items: center;
  color: #0a3d91;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.form-heading h2 {
  margin: 10px 0 0;
  color: #14213d;
  font-size: clamp(1.55rem, 3vw, 2.35rem);
  font-weight: 650;
  line-height: 1.08;
}

.form-heading p {
  margin: 12px 0 0;
  color: #5b5b57;
  line-height: 1.7;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.form-grid label {
  display: grid;
  gap: 8px;
  margin: 0;
}

.form-grid label.full {
  grid-column: 1 / -1;
}

.form-grid label span {
  color: #434341;
  font-size: 0.84rem;
  font-weight: 800;
}

.form-grid input,
.form-grid select,
.form-grid textarea {
  color-scheme: light;
  width: 100%;
  min-height: 46px;
  padding: 11px 12px;
  color: #14213d;
  border: 1px solid #dbdbdb;
  border-radius: 8px;
  background-color: #ffffff;
  font: inherit;
  outline: none;
  transition: border-color 160ms ease, box-shadow 160ms ease;
}

.form-grid textarea {
  resize: vertical;
}

.form-grid input:focus,
.form-grid select:focus,
.form-grid textarea:focus {
  border-color: #0a3d91;
  box-shadow: 0 0 0 3px rgba(10, 61, 145, 0.1);
}

.phone-row {
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr);
  gap: 8px;
}

.phone-row .phone-code {
  padding-left: 10px;
  padding-right: 28px;
  text-align: left;
  text-align-last: left;
}

.form-alert {
  margin: 18px 0 0;
  padding: 12px 14px;
  border-radius: 8px;
  font-weight: 800;
  line-height: 1.35;
}

.form-alert.success {
  color: #166534;
  background-color: #dcfce7;
}

.form-alert.error {
  color: #991b1b;
  background-color: #fee2e2;
}

.submit-button {
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 22px;
  padding: 13px 20px;
  color: #ffffff;
  border: 0;
  border-radius: 999px;
  background-color: #14213d;
  font-size: 0.92rem;
  font-weight: 800;
  transition: transform 180ms ease, background-color 180ms ease;
}

.submit-button:hover:not(:disabled) {
  background-color: #0a3d91;
  transform: translateY(-2px);
}

.submit-button:disabled {
  cursor: not-allowed;
  opacity: 0.72;
}

@media (max-width: 640px) {
  .demo-modal-backdrop {
    padding: 12px;
  }

  .demo-form {
    padding: 22px;
  }

  .form-grid,
  .phone-row {
    grid-template-columns: 1fr;
  }

  .submit-button {
    width: 100%;
  }
}
</style>
